import { useEffect, useState } from 'react';
import { ExternalLink, Package, Plug, RefreshCw, Trash } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  useDropiStatus,
  useRemoveDropiConfig,
  useSaveDropiConfig,
  useSincronizarStockDropi,
  useTestDropiConnection,
} from './use-dropi';
import type { DropiStockResumen } from '@/types/dropi';

/**
 * Conexión con Dropi (docs/DROPI.md) — la cuenta de dropshipping del negocio.
 *
 * Cada negocio pega SU token (se genera en el panel de Dropi: Integraciones → app → Generar token).
 * Dos cosas que esta tarjeta resuelve y que no son obvias:
 *
 *  1. **El «Origen autorizado»**: Dropi valida el header `Origin` contra la lista blanca que lleva
 *     DENTRO el token, y si no coincide responde un `Access denied` que no explica nada. Al probar
 *     la conexión el backend devuelve esa lista y acá se ofrece como selector, así nadie la adivina.
 *  2. **El token no se vuelve a mostrar**: el estado trae solo una máscara. Guardar hace MERGE, así
 *     que se puede apagar/encender o cambiar la etiqueta sin volver a pegarlo.
 *
 * Con esto conectado, cada producto del catálogo se puede vincular con su equivalente en Dropi y la
 * venta que cierra el bot se puede enviar a Dropi desde la pantalla de Órdenes.
 */
export function DropiCard() {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const status = useDropiStatus(tenantId);
  const guardar = useSaveDropiConfig(tenantId);
  const quitar = useRemoveDropiConfig(tenantId);
  const probar = useTestDropiConnection(tenantId);
  const stock = useSincronizarStockDropi(tenantId);

  const [token, setToken] = useState('');
  const [origin, setOrigin] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [activo, setActivo] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // El estado llega del backend una sola vez (sin pisar lo que el usuario esté escribiendo).
  useEffect(() => {
    if (!status.data) return;
    setOrigin((a) => a || status.data?.origin || '');
    setEtiqueta((a) => a || status.data?.etiqueta || '');
    setActivo((a) => (status.data ? status.data.activo : a));
  }, [status.data]);

  // Si la prueba sale bien, el backend devuelve los orígenes del token: se propone el que se usó.
  useEffect(() => {
    if (probar.data?.ok && !origin.trim()) setOrigin(probar.data.originUsado);
  }, [probar.data, origin]);

  const configurada = Boolean(status.data?.configurada);
  const origenes = probar.data?.origenes ?? status.data?.origenes ?? [];

  const errorGuardar = guardar.isError ? getErrorMessage(guardar.error) : null;
  const errorProbar = probar.isError ? getErrorMessage(probar.error) : null;

  /** Guarda: manda el token solo si se escribió uno nuevo (el backend mergea el resto). */
  async function handleGuardar() {
    if (guardar.isPending) return;
    guardar.reset();

    await guardar
      .mutateAsync({
        ...(token.trim() ? { token: token.trim() } : {}),
        ...(origin.trim() ? { origin: origin.trim() } : {}),
        etiqueta: etiqueta.trim(),
        activo,
      })
      .then(() => setToken(''))
      .catch(() => undefined);
  }

  /** Prueba sin guardar: sirve para validar el token recién pegado. */
  async function handleProbar() {
    if (probar.isPending) return;
    probar.reset();

    await probar
      .mutateAsync({
        ...(token.trim() ? { token: token.trim() } : {}),
        ...(origin.trim() ? { origin: origin.trim() } : {}),
      })
      .catch(() => undefined);
  }

  async function handleQuitar() {
    await quitar.mutateAsync().catch(() => undefined);
    setConfirmRemove(false);
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="grid size-10 place-items-center rounded-control bg-brand-50 text-brand-700">
                <Package size={20} aria-hidden="true" />
              </span>
              <div>
                <CardTitle>Dropi</CardTitle>
                <CardDescription>
                  Enviá las ventas del bot a tu cuenta de Dropi y mirá la huella digital del cliente
                  al lado de cada orden.
                </CardDescription>
              </div>
            </div>
            <Badge tone={configurada ? (status.data?.activo ? 'success' : 'warning') : 'neutral'} dot>
              {configurada ? (status.data?.activo ? 'Conectado' : 'Pausado') : 'Sin conectar'}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {configurada ? (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-ink-muted">
              <span>
                Token: <code className="font-mono">{status.data?.previewToken}</code>
              </span>
              {status.data?.aplicacion ? <span>· App: {status.data.aplicacion}</span> : null}
              {status.data?.dropiUserId ? (
                <span>· Cuenta Dropi: {status.data.dropiUserId}</span>
              ) : null}
            </div>
          ) : null}

          <Field
            label="Token de integración"
            htmlFor="dropiToken"
            hint="En Dropi: Integraciones → tu app → Generar token. Se guarda cifrado y no se vuelve a mostrar."
          >
            <PasswordInput
              id="dropiToken"
              name="token"
              autoComplete="off"
              placeholder={
                configurada ? 'Pegá uno nuevo solo si querés reemplazarlo' : 'eyJ0eXAiOiJKV1…'
              }
              disabled={!canEdit || guardar.isPending}
              value={token}
              onChange={(evento) => setToken(evento.target.value)}
            />
          </Field>

          <Field
            label="Origen autorizado"
            htmlFor="dropiOrigin"
            hint="Dropi solo responde si el origen coincide con la lista blanca del token. Probá la conexión y elegí uno de la lista."
          >
            {origenes.length > 0 ? (
              <Select
                id="dropiOrigin"
                value={origin}
                disabled={!canEdit || guardar.isPending}
                onChange={(evento) => setOrigin(evento.target.value)}
              >
                <option value="">Elegí un origen…</option>
                {origenes.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                id="dropiOrigin"
                name="origin"
                placeholder="https://tu-app.com"
                leftIcon={<Plug size={16} />}
                disabled={!canEdit || guardar.isPending}
                value={origin}
                onChange={(evento) => setOrigin(evento.target.value)}
              />
            )}
          </Field>

          <Field
            label="Nombre de la app (opcional)"
            htmlFor="dropiEtiqueta"
            hint="Solo para reconocerla en el panel."
          >
            <Input
              id="dropiEtiqueta"
              name="etiqueta"
              placeholder="Ej. Naturgen 1"
              disabled={!canEdit || guardar.isPending}
              value={etiqueta}
              onChange={(evento) => setEtiqueta(evento.target.value)}
            />
          </Field>

          <Field
            label="Integración activa"
            htmlFor="dropiActivo"
            hint="Apagada: no se envían órdenes ni se consulta la huella, pero el token se conserva."
          >
            <Switch
              id="dropiActivo"
              checked={activo}
              label="Integración activa"
              disabled={!canEdit || guardar.isPending}
              onChange={setActivo}
            />
          </Field>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              leftIcon={<Package size={16} />}
              loading={guardar.isPending}
              disabled={!canEdit || (!token.trim() && !configurada)}
              onClick={() => void handleGuardar()}
            >
              {configurada ? 'Guardar cambios' : 'Guardar y conectar'}
            </Button>

            <Button
              variant="secondary"
              loading={probar.isPending}
              disabled={!canEdit || (!token.trim() && !configurada)}
              onClick={() => void handleProbar()}
            >
              Probar conexión
            </Button>

            {configurada ? (
              <Button
                variant="secondary"
                leftIcon={<RefreshCw size={16} />}
                loading={stock.isPending}
                disabled={!canEdit}
                title="Trae el stock REAL de Dropi de los productos vinculados: con 0 unidades el bot deja de ofrecerlos. El sistema también lo hace solo cada pocas horas."
                onClick={() => void stock.mutateAsync()}
              >
                Traer stock de Dropi
              </Button>
            ) : null}

            {configurada ? (
              <Button
                variant="ghost"
                className="text-danger-strong hover:bg-danger-soft"
                leftIcon={<Trash size={16} />}
                disabled={!canEdit || guardar.isPending}
                onClick={() => setConfirmRemove(true)}
              >
                Desconectar
              </Button>
            ) : null}

            {!canEdit ? (
              <span className="text-caption text-ink-muted">
                Solo un administrador del negocio puede configurar esto.
              </span>
            ) : null}
          </div>

          {errorGuardar ? <Alert tone="warning">{errorGuardar}</Alert> : null}
          {errorProbar ? <Alert tone="warning">{errorProbar}</Alert> : null}
          {probar.data ? (
            <Alert tone={probar.data.ok ? 'success' : 'warning'}>{probar.data.mensaje}</Alert>
          ) : null}
          {stock.isError ? <Alert tone="warning">{getErrorMessage(stock.error)}</Alert> : null}
          {stock.data ? <Alert tone="success">{describeStockSync(stock.data)}</Alert> : null}
          {quitar.isError ? <Alert tone="warning">{getErrorMessage(quitar.error)}</Alert> : null}
          {guardar.isSuccess ? <Alert tone="success">Configuración guardada.</Alert> : null}

          <p className="text-caption text-ink-faint">
            <a
              href="https://dropi.co/integraciones/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline"
            >
              Ver las integraciones de Dropi
              <ExternalLink size={13} />
            </a>
          </p>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => void handleQuitar()}
        title="Desconectar Dropi"
        description="Se borra el token guardado y se dejan de enviar órdenes y de consultar la huella digital. Las órdenes que ya están en Dropi no se tocan."
        confirmLabel="Desconectar"
        loading={quitar.isPending}
        error={quitar.isError ? getErrorMessage(quitar.error) : null}
      />
    </>
  );
}

/**
 * Qué contó la sincronización de stock, en una frase.
 *
 * Se dice en positivo lo que importa: cuántos productos quedaron SIN unidades, porque son los que el
 * bot deja de ofrecer (que es el motivo por el que existe el botón).
 */
function describeStockSync(resumen: DropiStockResumen): string {
  const partes = [`Se revisaron ${resumen.revisados} producto(s) vinculado(s)`];

  partes.push(
    resumen.actualizados > 0
      ? `${resumen.actualizados} cambiaron de stock`
      : 'ninguno cambió de stock',
  );

  if (resumen.agotados > 0) {
    partes.push(`${resumen.agotados} quedó sin unidades (el bot ya no lo ofrece)`);
  }
  if (resumen.errores > 0) {
    partes.push(`${resumen.errores} no se pudo leer`);
  }

  return `${partes.join(' · ')}.`;
}